import React, { useEffect, useState } from 'react';
import { Modal, Form, Radio, Input, Button, Space, Typography, Alert } from 'antd';
import { putRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';

const { Text } = Typography;

export interface ParentPasswordTarget {
  parentId: number;
  name?: string;
  phoneNumber?: string;
}

interface ChangeParentPasswordModalProps {
  open: boolean;
  parentData: ParentPasswordTarget | null;
  onSuccess?: () => void;
  onClose: () => void;
}

export const ChangeParentPasswordModal: React.FC<ChangeParentPasswordModalProps> = ({
  open,
  parentData,
  onSuccess,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [mode, setMode] = useState<'custom' | 'phone'>('custom');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setMode('custom');
    }
  }, [open, form]);

  const handleFinish = async (values: any) => {
    if (!parentData) return;

    let newPassword = '';
    if (mode === 'phone') {
      if (!parentData.phoneNumber) {
        toast.error('Phụ huynh này chưa có số điện thoại!');
        return;
      }
      newPassword = parentData.phoneNumber.replace(/\s+/g, '');
    } else {
      const pwd = values.password?.trim();
      if (!pwd) {
        toast.error('Vui lòng nhập mật khẩu mới!');
        return;
      }
      if (pwd.length < 6) {
        toast.error('Mật khẩu mới phải có tối thiểu 6 ký tự!');
        return;
      }
      newPassword = pwd;
    }

    setSubmitting(true);
    try {
      const url = `/cms/admin/customer/update-parent-password/${parentData.parentId}?newPassword=${encodeURIComponent(newPassword)}`;
      const res: ResponseDataType<any> = await putRequest(url);
      if (res?.code === 200) {
        toast.success(
          mode === 'phone'
            ? `Đổi mật khẩu thành công! Mật khẩu mới là SĐT: ${newPassword}`
            : 'Đổi mật khẩu thành công!'
        );
        form.resetFields();
        onSuccess?.();
        onClose();
      } else {
        toast.error(res?.msg || 'Đổi mật khẩu thất bại!');
      }
    } catch (error) {
      toast.error('Có lỗi xảy ra khi đổi mật khẩu!');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Đổi mật khẩu phụ huynh"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={null}
      destroyOnClose
      width={460}
    >
      <div style={{ marginTop: 12, marginBottom: 16 }}>
        <div style={{ background: '#f5f5f5', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>
          <div>
            <Text type="secondary">Phụ huynh: </Text>
            <Text strong>{parentData?.name || '---'}</Text>
          </div>
          <div>
            <Text type="secondary">Số điện thoại: </Text>
            <Text strong>{parentData?.phoneNumber || 'Chưa cập nhật'}</Text>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ mode: 'custom' }}
        >
          <Form.Item label="Chọn hình thức đổi mật khẩu" style={{ marginBottom: 14 }}>
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
            >
              <Radio value="custom">
                <span>Nhập mật khẩu mới</span>
              </Radio>
              <Radio value="phone" disabled={!parentData?.phoneNumber}>
                <span>Lấy theo số điện thoại ({parentData?.phoneNumber || 'Chưa có SĐT'})</span>
              </Radio>
            </Radio.Group>
          </Form.Item>

          {mode === 'custom' && (
            <Form.Item
              name="password"
              label="Mật khẩu mới"
              rules={[
                { required: true, message: 'Vui lòng nhập mật khẩu mới' },
                { min: 6, message: 'Mật khẩu phải từ 6 ký tự trở lên' },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input.Password placeholder="Nhập mật khẩu mới (tối thiểu 6 ký tự)" autoFocus />
            </Form.Item>
          )}

          {mode === 'phone' && (
            <Alert
              type="info"
              showIcon
              message={
                <div>
                  Mật khẩu mới sẽ được đặt là số điện thoại: <strong>{parentData?.phoneNumber}</strong>
                </div>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose} disabled={submitting}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Xác nhận
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};
